import { createContext, useContext, useEffect, useState } from 'react'

import { modalEventDispatch, modalEventListener } from '@space-events/modal'

import { CommunitiesActions } from '@space-shell/common/services/communities'

import { CommunityEventType } from './types'
import type {
  CommunityModalContextProps,
  CommunityModalProviderProps,
  CommunityModalEvent
} from './types'

import CommunityModal from '../../communityModal'
import type { CommunityModalType } from '../../types'

const CommunityModalContext = createContext<CommunityModalContextProps>(
  {} as CommunityModalContextProps
)

export const CommunityModalProvider = ({ children }: CommunityModalProviderProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [modalType, setModalType] = useState<CommunityModalType>(CommunitiesActions.ADD)
  const [communityId, setCommunityId] = useState<number>()
  const [groupId, setGroupId] = useState<number>(0)

  const resetState = () => {
    setIsOpen(false)
    setModalType(CommunitiesActions.ADD)
    setCommunityId(undefined)
    setGroupId(0)
  }

  useEffect(() => {
    /* istanbul ignore next */
    modalEventListener((event) => {
      const payload = (event as CustomEvent<CommunityModalEvent>).detail

      switch (payload.eventType) {
        case CommunityEventType.CLOSE:
          window.document.body.classList.remove('_overflow-hidden')
          resetState()
          break
        case CommunityEventType.OPEN:
          payload?.modalType && setModalType(payload.modalType)
          payload?.communityId && setCommunityId(payload?.communityId)
          payload?.groupId !== undefined && setGroupId(payload?.groupId)
          window.document.body.classList.add('_overflow-hidden')
          setIsOpen(true)
          break
      }
    })
  }, [])

  const dispatchModalEvent = (eventType: CommunityEventType) => {
    modalEventDispatch<CommunityModalEvent>({ eventType, modalType })
  }

  const closeModal = () => {
    resetState()
    dispatchModalEvent(CommunityEventType.CLOSE)
  }

  return (
    <CommunityModalContext.Provider
      value={{
        isOpen,
        setModalType,
        dispatchModalEvent,
        communityId,
        setCommunityId
      }}>
      <CommunityModal
        communityId={communityId}
        groupId={groupId}
        modalType={modalType}
        isOpen={isOpen}
        closeModal={closeModal}
      />
      {children}
    </CommunityModalContext.Provider>
  )
}

export const useCommunityModal = () => {
  const context = useContext(CommunityModalContext)

  if (!context) {
    throw new Error('useCommunityModal must be used inside CommunityModalProvider')
  }

  return context
}
